import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import PlanInfoCard from "./lessonplans/PlanInfoCard";
import StandardsCard from "./lessonplans/StandardsCard";
import TemplateCard from "./lessonplans/TemplateCard";
import ScheduleCard from "./lessonplans/ScheduleCard";
import ResultCard from "./lessonplans/ResultCard";
import SavedPlansCard from "./lessonplans/SavedPlansCard";
import { newDay, schoolDays } from "./lessonplans/dates";

const EMPTY_HEADER = { school: "", teacher: "", className: "", period: "50", weekStart: "", weekEnd: "", nextAssessment: "", nextAssessmentDate: "", standards: "", objectives: "", aiStandards: true, aiObjectives: true };

export default function LessonPlansTab() {
  const [header, setHeader] = useState(EMPTY_HEADER);
  const [catalog, setCatalog] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [saved, setSaved] = useState([]);
  const [days, setDays] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [openDay, setOpenDay] = useState(0);
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState(null);
  const dirtyRef = useRef(false);
  const autoPickedRef = useRef(false);

  const load = async () => {
    try {
      const [s, c, t, p] = await Promise.all([
        api.get("/lesson-plans/settings"), api.get("/lesson-plans/catalog"),
        api.get("/lesson-plans/templates"), api.get("/lesson-plans"),
      ]);
      if (!dirtyRef.current) setHeader((h) => ({ ...h, ...s.data }));
      setCatalog(c.data.tracks || []);
      setTemplates(t.data.templates || []);
      setSaved(p.data.plans || []);
      if (!autoPickedRef.current && t.data.templates?.length) setTemplateId(t.data.templates[0].id);
      autoPickedRef.current = true;
    } catch (e) { toast.error("Could not load your lesson-plan data."); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const updateHeader = (fn) => { dirtyRef.current = true; setHeader(fn); };

  useEffect(() => {
    if (!header.weekStart) return;
    const wanted = schoolDays(header.weekStart, header.weekEnd);
    setDays((ds) => wanted.map((iso) => ds.find((d) => d.date === iso) || newDay(iso)));
  }, [header.weekStart, header.weekEnd]);

  const generate = async () => {
    setGenerating(true); setResult(null); setSavedId(null);
    const { school, teacher, className, period, standards, objectives, aiStandards, aiObjectives } = header;
    api.put("/lesson-plans/settings", { school, teacher, className, period, standards, objectives, aiStandards, aiObjectives }).catch(() => {});
    try {
      const schedule = days.map((d) => ({ date: d.date, items: d.items.map(({ track, block, kind, id }) => ({ track, block, kind, id })) }));
      const r = await api.post("/lesson-plans/generate-from-schedule", { schedule, template_id: templateId || null, header });
      setResult(r.data); setOpenDay(0);
      toast.success(`Drafted ${r.data.plans.length} day${r.data.plans.length > 1 ? "s" : ""} — every box filled.`);
    } catch (err) { toast.error(err.response?.data?.detail || "Generation failed"); }
    finally { setGenerating(false); }
  };

  const savePlan = async () => {
    if (!result) return;
    setSaving(true);
    try {
      const r = await api.post("/lesson-plans", { name: "", template_id: templateId || null, header, labels: result.labels, plans: result.plans });
      setSavedId(r.data.id);
      toast.success(`Saved "${r.data.name}" — download below.`);
      load();
    } catch (e) { toast.error("Could not save."); }
    finally { setSaving(false); }
  };

  const download = (planId, dayIdx) => {
    const url = dayIdx == null ? `${api.defaults.baseURL}/lesson-plans/${planId}/download-week` : `${api.defaults.baseURL}/lesson-plans/${planId}/download/${dayIdx}`;
    const a = document.createElement("a"); a.href = url; a.download = ""; document.body.appendChild(a); a.click(); a.remove();
  };
  const deletePlan = async (id) => { await api.delete(`/lesson-plans/${id}`); if (savedId === id) setSavedId(null); load(); };

  return (
    <div className="space-y-6" data-testid="lesson-plans-tab">
      <div>
        <h2 className="font-display text-2xl text-slate-100">Lesson Plan Creator</h2>
        <p className="text-sm text-muted-foreground max-w-2xl">Fill in your plan details, pick the week, choose the HorizonQuest work for each day, and I'll draft one complete plan per day — labeled by weekday and date — in your own template.</p>
      </div>
      <PlanInfoCard header={header} setHeader={updateHeader} />
      <StandardsCard header={header} setHeader={updateHeader} />
      <TemplateCard templates={templates} templateId={templateId} setTemplateId={setTemplateId} reload={load} />
      <ScheduleCard days={days} setDays={setDays} catalog={catalog} onGenerate={generate} generating={generating} />
      {result && <ResultCard result={result} setResult={setResult} openDay={openDay} setOpenDay={setOpenDay} onSave={savePlan} saving={saving} savedId={savedId} onDownload={download} />}
      <SavedPlansCard saved={saved} onDownload={download} onDelete={deletePlan} />
    </div>
  );
}
